import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    AlertCircle,
    ArrowLeft,
    CheckCircle2,
    Clock,
    ExternalLink,
    PauseCircle,
    RefreshCw,
    SquarePen,
    Trash2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { UpgradeNoticeSnackbar } from '../../../../components/UpgradeNoticeSnackbar';
import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { SocialAccount, SocialPost, SocialPostMetric } from '../../../../common/models/social-scheduler.model';
import { CsAsset } from '../../../../common/models/content-studio.model';
import { PlatformIcon } from '../../../../modules/vendor/social-scheduler/components/PlatformIcon';
import { PostForm, PostFormValues } from '../../../../modules/vendor/social-scheduler/components/PostForm';
import { PageContainer } from '../../../../common/components/PageContainer';
import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar-icon.svg';
import { formatPostTime } from '../../../../modules/vendor/social-scheduler/format-time';

// Status tint + text from the shared token set so every status reads in both themes.
const statusStyles: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground',
    scheduled: 'bg-muted text-foreground',
    on_hold: 'bg-warning/10 text-warning',
    publishing: 'bg-warning/10 text-warning',
    published: 'bg-success/10 text-success',
    failed: 'bg-destructive/10 text-destructive',
};

const statusIcons: Record<string, React.ElementType> = {
    draft: SquarePen,
    scheduled: Clock,
    on_hold: PauseCircle,
    publishing: RefreshCw,
    published: CheckCircle2,
    failed: AlertCircle,
};

const MetricCard: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
    <div className="rounded-xl border bg-card p-4 text-center">
        <div className="text-2xl font-medium text-foreground">
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
);

export const SocialPostDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const api = useSocialSchedulerApi();
    const csApi = useContentStudioApi();

    const [post, setPost] = useState<SocialPost | null>(null);
    const [metrics, setMetrics] = useState<SocialPostMetric | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const [assets, setAssets] = useState<CsAsset[]>([]);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadData = useCallback(async () => {
        if (!uuid) return;
        try {
            const [p, accRes, assetRes] = await Promise.all([
                api.getPost(uuid),
                api.listAccounts({ status: 'active' }),
                csApi.listAssets({ status: 'ready', limit: 100 } as any),
            ]);
            setPost(p);
            setAccounts(accRes.items || []);
            setAssets((assetRes as any).items || []);
            if (p.status === 'published') {
                const m = await api.getPostMetrics(uuid);
                setMetrics(m);
            }
        } catch (err) {
            console.error('Failed to load post:', err);
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => { loadData(); }, [loadData]);

    const [upgradeNotice, setUpgradeNotice] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    });

    const handleEditSubmit = async (values: PostFormValues, asDraft: boolean) => {
        if (!uuid) return;
        setSaving(true);
        try {
            await api.updatePost(uuid, {
                caption: values.caption,
                hashtags: values.hashtags,
                scheduledAt: values.scheduledAt ? new Date(values.scheduledAt).toISOString() : undefined,
            });
            if (asDraft && post?.status === 'scheduled') {
                // "Save as draft" on a scheduled post → unschedule it
                await api.unschedulePost(uuid);
            } else if (!asDraft && post?.status === 'draft') {
                // "Schedule" on a draft post → schedule it
                await api.schedulePost(uuid);
            }
            navigate('/marketing/social-scheduler?tab=posts');
        } catch (err: any) {
            // Surface the backend error rather than failing silently. The plan-cap
            // case (maxScheduledPostsPerMonth) returns 403 with the standard
            // ForbiddenException payload from VendorQuotaGuard.
            let message = t(
                'SOCIAL_SCHEDULER.SAVE_FAILED',
                'Could not save the post. Please try again.',
            );
            try {
                if (err && typeof err.json === 'function') {
                    const body = await err.json();
                    if (err.status === 403 && body?.quotaType === 'maxScheduledPostsPerMonth') {
                        message = t(
                            'SOCIAL_SCHEDULER.POSTS_CAP_REACHED',
                            'You\'ve reached your plan\'s limit of {{cap}} scheduled post(s) this month. Upgrade to schedule more.',
                            { cap: body.limit },
                        );
                    } else {
                        const raw = body?.message ?? body?.error ?? message;
                        message = Array.isArray(raw) ? raw.join(' • ') : String(raw);
                    }
                } else if (err?.message) {
                    message = String(err.message);
                }
            } catch {
                // body wasn't JSON — keep fallback
            }
            setUpgradeNotice({ open: true, message });
            console.error('Failed to save:', err);
        } finally {
            setSaving(false);
        }
    };

    const requestDelete = () => setConfirmDeleteOpen(true);

    const confirmDelete = async () => {
        if (!uuid) return;
        setConfirmDeleteOpen(false);
        try {
            await api.deletePost(uuid);
        } catch (err: any) {
            // The platform can refuse (Instagram always does): say what it said, as the list does.
            toast.error(
                err?.response?.data?.message
                || err?.message
                || (t('SOCIAL_SCHEDULER.DELETE_FAILED', 'That post could not be deleted.') as string),
                { duration: 10000 },
            );
            return;
        }
        navigate('/marketing/social-scheduler?tab=posts');
    };

    if (loading || !post) {
        return (
            <PageContainer
                breadcrumbs={[
                    { label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') },
                    {
                        label: t('SIDEBAR.MARKETING.PLANNER', 'Planner'),
                        icon: <CalendarIcon style={{ width: 20, height: 20 }} />,
                    },
                    { label: t('SOCIAL_SCHEDULER.EDIT_POST') },
                ]}
            >
                <div className="py-16 text-center text-sm text-muted-foreground">
                    {loading ? t('SOCIAL_SCHEDULER.LOADING', 'Loading...') : t('SOCIAL_SCHEDULER.POST_NOT_FOUND', 'Post not found')}
                </div>
            </PageContainer>
        );
    }

    const isEditable = ['draft', 'scheduled'].includes(post.status);
    const statusClass = statusStyles[post.status] || statusStyles.draft;
    const StatusIcon = statusIcons[post.status];
    const timestampIso = post.status === 'published' && post.publishedAt ? post.publishedAt : post.scheduledAt;
    const scheduledDate = formatPostTime(timestampIso);
    const scheduledAbsolute = new Date(timestampIso).toLocaleString();

    const toLocalDatetime = (iso: string) => {
        const d = new Date(iso);
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };

    const formInitial: Partial<PostFormValues> = {
        selectedAssetUuids: post.assetUuids?.length ? post.assetUuids : [post.assetUuid],
        platform: post.platform,
        accountUuid: post.accountUuid,
        caption: post.caption || '',
        hashtags: post.hashtags || [],
        scheduledAt: toLocalDatetime(post.scheduledAt),
    };

    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE') }, { label: t('SIDEBAR.MARKETING.PLANNER'), icon: <CalendarIcon style={{ width: 20, height: 20 }} /> }, { label: t('SOCIAL_SCHEDULER.EDIT_POST') }]}>
        <div className="mx-auto max-w-[800px]">
            {/* Header */}
            <div className="mb-6 flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => navigate('/marketing/social-scheduler?tab=posts')}
                >
                    <ArrowLeft className="rtl:rotate-180" />
                    {t('SOCIAL_SCHEDULER.BACK', 'Back')}
                </Button>
                <div className="flex-1" />
                {['draft', 'scheduled', 'failed', 'published'].includes(post.status) && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={requestDelete}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                        <Trash2 />
                        {t('SOCIAL_SCHEDULER.DELETE', 'Delete')}
                    </Button>
                )}
            </div>

            {/* ── Editable posts: show the full form (same as create) ─── */}
            {isEditable && (
                <PostForm
                    accounts={accounts}
                    assets={assets}
                    initial={formInitial}
                    lockTargeting={true}
                    onSubmit={handleEditSubmit}
                    submitLabel={t('SOCIAL_SCHEDULER.SAVE', 'Save changes') || undefined}
                    draftLabel={t('SOCIAL_SCHEDULER.SAVE_DRAFT', 'Save as draft') || undefined}
                    submitting={saving}
                />
            )}

            {/* ── Non-editable posts (published/publishing/failed): read-only view ─── */}
            {!isEditable && (
                <>
                    <div className="mb-6 rounded-xl border bg-card p-6">
                        <div className="mb-4 flex items-center gap-3">
                            <PlatformIcon platform={post.platform} size={28} />
                            <span className={cn('flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium', statusClass)}>
                                {StatusIcon && (
                                    <StatusIcon
                                        aria-hidden
                                        className={cn('size-4', post.status === 'publishing' && 'animate-spin')}
                                    />
                                )}
                                {t(`SOCIAL_SCHEDULER.STATUS.${post.status.toUpperCase()}`, post.status)}
                            </span>
                            <div className="flex-1" />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="cursor-help text-xs text-muted-foreground">
                                        {post.status === 'published'
                                            ? t('SOCIAL_SCHEDULER.PUBLISHED_AT', 'Published')
                                            : t('SOCIAL_SCHEDULER.SCHEDULED_FOR', 'Scheduled')}
                                        {' · '}
                                        {scheduledDate}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">{scheduledAbsolute}</TooltipContent>
                            </Tooltip>
                        </div>

                        <p className="mb-4 text-sm leading-relaxed whitespace-pre-wrap text-foreground">
                            {post.caption || t('SOCIAL_SCHEDULER.NO_CAPTION', 'No caption')}
                        </p>

                        {post.hashtags?.length > 0 && (
                            <div className="mb-4 flex flex-wrap gap-1">
                                {post.hashtags.map((h) => (
                                    <Badge key={h} variant="outline">#{h}</Badge>
                                ))}
                            </div>
                        )}

                        {post.permalink && (
                            <a
                                href={post.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
                            >
                                <ExternalLink className="size-3.5" />
                                {post.platform === 'instagram'
                                    ? t('SOCIAL_SCHEDULER.VIEW_ON_INSTAGRAM', 'View on Instagram')
                                    : t('SOCIAL_SCHEDULER.VIEW_ON_FACEBOOK', 'View on Facebook')}
                            </a>
                        )}

                        {post.error && (
                            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                                {post.error}
                            </div>
                        )}
                    </div>

                    {/*
                      * Engagement, including before there is any.
                      *
                      * The section used to render only once a metrics row
                      * existed, so a post published minutes ago showed nothing
                      * at all — no panel, no explanation. That reads as a
                      * broken feature rather than a measurement that has not
                      * happened yet, which is exactly how it was first
                      * reported. Collection runs hourly; saying so is the
                      * difference between "not yet" and "not working".
                      */}
                    {post.status === 'published' && !metrics && (
                        <div>
                            <h2 className="font-heading mb-3 text-lg font-medium tracking-tight text-foreground">
                                {t('SOCIAL_SCHEDULER.ENGAGEMENT', 'Engagement')}
                            </h2>
                            <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                                {t(
                                    'SOCIAL_SCHEDULER.METRICS_PENDING',
                                    'Not measured yet. Engagement is collected from the platform once an hour.',
                                )}
                            </div>
                        </div>
                    )}

                    {post.status === 'published' && metrics && (
                        <div>
                            <h2 className="font-heading mb-3 text-lg font-medium tracking-tight text-foreground">
                                {t('SOCIAL_SCHEDULER.ENGAGEMENT', 'Engagement')}
                            </h2>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <MetricCard label={t('SOCIAL_SCHEDULER.IMPRESSIONS', 'Impressions')} value={metrics.impressions} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.REACH', 'Reach')} value={metrics.reach} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.LIKES', 'Likes')} value={metrics.likes} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.COMMENTS', 'Comments')} value={metrics.comments} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.SHARES', 'Shares')} value={metrics.shares} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.SAVES', 'Saves')} value={metrics.saves} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.CLICKS', 'Clicks')} value={metrics.clicks} />
                                <MetricCard label={t('SOCIAL_SCHEDULER.ENGAGEMENT_RATE', 'Engagement %')} value={`${metrics.engagementRate}%`} />
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Delete post confirmation */}
            <Dialog open={confirmDeleteOpen} onOpenChange={(next) => { if (!next) setConfirmDeleteOpen(false); }}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>{t('SOCIAL_SCHEDULER.DELETE_POST_TITLE', 'Delete post?')}</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                        {post.status === 'published'
                            ? (post.platform === 'instagram'
                                ? t('SOCIAL_SCHEDULER.DELETE_PUBLISHED_INSTAGRAM_MSG', 'Instagram does not let apps delete a published post. Delete it in the Instagram app; it stays listed here until then.')
                                : t('SOCIAL_SCHEDULER.DELETE_PUBLISHED_FACEBOOK_MSG', 'This post will also be deleted from your Facebook Page. This action cannot be undone.'))
                            : t('SOCIAL_SCHEDULER.DELETE_POST_MSG', 'This post will be permanently deleted. This action cannot be undone.')}
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" className="h-9" onClick={() => setConfirmDeleteOpen(false)}>
                            {t('SOCIAL_SCHEDULER.CANCEL', 'Cancel')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={confirmDelete}
                            className="h-9 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                            {t('SOCIAL_SCHEDULER.DELETE_POST', 'Delete post')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <UpgradeNoticeSnackbar
                open={upgradeNotice.open}
                message={upgradeNotice.message}
                onClose={() => setUpgradeNotice((prev) => ({ ...prev, open: false }))}
            />
        </div>
        </PageContainer>
    );
};
